import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction } from "core/types";
import { Card } from "ds_legacy/components/InfoCard/index";
import { Divider, ResponsiveLayout } from "ds_legacy/materials/layouts";
import { margin } from "ds_legacy/materials/metrics";
import { Subheading, Title } from "ds_legacy/materials/typography";
import { useEncryption } from "dsl/atoms/Contexts";
import {
  BooleanField,
  CategoryContainer,
  EncryptedField,
  OntologyField,
  StringField,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import { useTranslations } from "translations";

function TransportOrganizedByInfo({
  title,
  transportOrganizer,
}: {
  title: string;
  transportOrganizer: string;
}) {
  return (
    <StringField
      prefix={title}
      testId="transport_organiser"
      value={transportOrganizer}
    />
  );
}

function AccompanyingPersonInfo({ auction }: { auction: Auction }) {
  const translations = useTranslations();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();

  const data = auction.patient.transport;
  if (!data) return null;

  const { accompanying_person, require_accompanying_person } = data;
  if (!require_accompanying_person) return null;

  return (
    <EncryptedField
      isSealdOnly={isSealdOnly}
      longContext
      prefix={translations.patient.transport.accompanyingPersonNecessary}
      testId="accompanying_person"
      value={accompanying_person}
      withEncryptionAccess={withEncryptionAccess}
    />
  );
}

export function TransportInfoCard({
  auction,
  forCareprovider = false,
}: {
  auction: Auction;
  forCareprovider?: boolean;
}) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const data = auction.patient.transport;

  if (!data) return null;

  const {
    daily_transport_needed,
    require_accompanying_person,
    transport_organiser,
    transport_type,
  } = data;

  if (
    !transport_type &&
    !transport_organiser &&
    !daily_transport_needed &&
    !require_accompanying_person
  ) {
    return null;
  }

  if (forCareprovider) {
    return (
      <>
        <Divider />
        <Subheading bold>
          {translations.patient.transport.typeOfTransport}
        </Subheading>
        <ResponsiveLayout>
          <CategoryContainer testId="transport_type">
            <OntologyField
              getOntology={getOntology}
              type="transportType"
              value={transport_type}
              verticalLayout
            />
          </CategoryContainer>
          <CategoryContainer testId="daily_transport_needed">
            <BooleanField
              label={translations.patient.transport.dailyTransportNeeded}
              value={daily_transport_needed}
            />
          </CategoryContainer>
        </ResponsiveLayout>
        <ResponsiveLayout>
          <CategoryContainer testId="accompanying_person">
            <AccompanyingPersonInfo auction={auction} />
          </CategoryContainer>
          <CategoryContainer testId="transport_organiser">
            {transport_organiser && (
              <TransportOrganizedByInfo
                title={translations.patient.transport.organizedBy.title}
                transportOrganizer={transport_organiser}
              />
            )}
          </CategoryContainer>
        </ResponsiveLayout>
      </>
    );
  }

  return (
    <Card noBorder width="100%">
      <Title>{translations.patient.transport.stepTitle}</Title>
      <OntologyField
        getOntology={getOntology}
        type="transportType"
        value={transport_type}
        verticalLayout
      />
      <BooleanField
        label={translations.patient.transport.dailyTransportNeeded}
        value={daily_transport_needed}
      />
      <AccompanyingPersonInfo auction={auction} />
      {transport_organiser && (
        <TransportOrganizedByInfo
          title={translations.patient.transport.organizedBy.title}
          transportOrganizer={transport_organiser}
        />
      )}
      <Divider margin={margin(2, 0, 0, 2)} />
    </Card>
  );
}
